/* eslint-disable max-lines-per-function */
import {
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Tooltip,
  Typography
} from '@material-ui/core';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import {
  Add,
  Cancel,
  Delete,
  Edit,
  Person,
  Save,
  Warning
} from '@material-ui/icons';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Notification} from '../classes/Notification';
import locale from '../locale/Localization';
import {getSuccessor, saveSuccessor} from '../script/person-service';
import {addNotifications} from '../store/appActions';
import {USER_TYPE_ADMIN, USER_TYPE_STATE_NOTARY} from '../utils/constants.js';
import ConfirmationDialog from './ConfirmationDialog';
import NotarySuccessorDialog from './NotarySuccessorDialog.jsx';

const useStyles = makeStyles((theme) => ({
  title: {paddingTop: 0, paddingBottom: '1%', fontWeight: 550},
  paper: {padding: theme.spacing(3, 2), width: 'inherit', marginTop: '0.1em'},
  textField: {
    marginRight: theme.spacing(1),
    width: 250,
    marginLeft: 10,
    paddingBottom: 15
  },
  list: {backgroundColor: theme.palette.background.default},
  listIcon: {
    color: 'rgba(0, 0, 0, 0.54)',
    padding: 12,
    marginRight: 10,
    overflow: 'visible'
  },
  submitButton: {float: 'right'}
}));

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9'
  }
}))(Tooltip);

/**
 * Notary contract field.
 * @param {P} props the props.
 * @return {*} the rendered string.
 */
function NotarySuccessor(props) {
  const classes = useStyles();
  const [accessDate, setAccessDate] = useState(null);
  const [acceptSuccessorSignature, setAcceptSuccessorSignature] =
    useState(false);
  const [dialogSuccessor, setDialogSuccessor] = useState(null);
  const [dialogAccessDate, setDialogAccessDate] = useState(null);
  const [deleteSuccessor, setDeleteSuccessor] = useState(false);
  const [enableSaveButton, setEnableSaveButton] = useState(false);
  const [openSuccessorDialog, setOpenSuccessorDialog] = useState(false);
  const [showSuccessorSubmitDialog, setShowSuccessorSubmitDialog] =
    useState(false);
  const [successor, setSuccessor] = useState(null);

  const addSuccessorNotifications = (notifications) =>
    props.dispatch(addNotifications(notifications));

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    if (props.notaryId) {
      getSuccessor(props.notaryId).then((response) => {
        if (response.hasSuccessor) {
          const suc = {
            id: response.successorId,
            officialName: response.officialName,
            firstNames: response.firstNames,
            organisation: {
              name: response.organisationName
            }
          };
          setSuccessor(suc);
          setAccessDate(response.writePermissionsUntil);
          setDialogSuccessor(suc);
          setDeleteSuccessor(false);
          setDialogAccessDate(response.writePermissionsUntil);
          setAcceptSuccessorSignature(response.allowSuccessorSignatur);
        } else {
          setSuccessor(null);
        }
        setEnableSaveButton(false);
      });
    }
  };

  const handleSuccessorSelection = (acceptSuccessorSignature) => {
    setSuccessor(dialogSuccessor);
    setAccessDate(dialogAccessDate);
    setEnableSaveButton(true);
    setOpenSuccessorDialog(false);
    setAcceptSuccessorSignature(acceptSuccessorSignature);
  };

  const save = () => {
    const newSuccessor = deleteSuccessor
      ? {deleteSuccessor: true}
      : {
          successorId: successor.id,
          writePermissionsUntil: accessDate,
          acceptSuccessorSignature: acceptSuccessorSignature
        };
    saveSuccessor(props.notaryId, newSuccessor).then((response) => {
      if (response.updated) {
        addSuccessorNotifications([
          new Notification(
            locale.successorUpdateCompleteNotification,
            'success',
            6000
          )
        ]);
        setDeleteSuccessor(false);
        setSuccessor(null);
        setEnableSaveButton(false);
        loadData();
        setShowSuccessorSubmitDialog(false);
      } else {
        addSuccessorNotifications([
          new Notification(
            locale.successorUpdateErrorNotification,
            'error',
            6000
          )
        ]);
      }
    });
  };

  const successorDescription = () => {
    if (!successor) {
      return '';
    }

    const successorOrganization = successor.organisation || {};
    const successorOrganizationName = successorOrganization.name;
    const description =
      successor.officialName +
      ', ' +
      successor.firstNames +
      (successorOrganizationName ? ' (' + successorOrganizationName + ')' : '');

    if (deleteSuccessor) {
      return <del>{description}</del>;
    }

    return description;
  };

  const successorAccessDate = () => {
    const hasAllowedType =
      props.user.type === USER_TYPE_ADMIN ||
      props.user.type === USER_TYPE_STATE_NOTARY;
    const displayDate = props.user.id === successor.id || hasAllowedType;
    const info = accessDate
      ? locale.formatString(
          locale.successorWriteAccessUntilDate,
          moment(accessDate).format(locale.localizedDateFormat)
        )
      : '';

    return displayDate ? info : null;
  };

  const refreshSuccessor = () => {
    loadData();
  };

  return (
    <Paper className={classes.paper}>
      <Typography variant='subtitle1' gutterBottom className={classes.title}>
        {locale.notarySuccessor}
      </Typography>
      {!successor && props.editMode && (
        <Button
          variant='contained'
          color='primary'
          size='small'
          onClick={() => {
            setOpenSuccessorDialog(true);
          }}
          startIcon={<Add />}
        >
          {locale.addButton}
        </Button>
      )}
      <Grid container item xs={12} spacing={3}>
        <Grid item xs={8}>
          {successor && (
            <div className={classes.list}>
              <List component='div' dense={true}>
                <ListItem className={classes.nested}>
                  <ListItemIcon>
                    <Person />
                  </ListItemIcon>
                  <ListItemText
                    primary={successorDescription()}
                    secondary={successorAccessDate()}
                  />
                  <ListItemSecondaryAction>
                    {enableSaveButton && (
                      <HtmlTooltip
                        className={classes.listIcon}
                        title={
                          <React.Fragment>
                            <Typography color='inherit'>
                              {locale.successorNotSubmittedTooltipTitle}
                            </Typography>
                            <div>
                              {locale.successorNotSubmittedTooltipDescription}
                            </div>
                          </React.Fragment>
                        }
                      >
                        <Warning />
                      </HtmlTooltip>
                    )}
                    {props.editMode && (
                      <IconButton
                        edge='end'
                        aria-label='function-edit'
                        disabled={deleteSuccessor}
                        onClick={() => {
                          setOpenSuccessorDialog(true);
                        }}
                      >
                        <Edit />
                      </IconButton>
                    )}
                    {props.editMode && !enableSaveButton && (
                      <IconButton
                        color='secondary'
                        variant='contained'
                        disabled={deleteSuccessor}
                        component='span'
                        aria-label='function-reset'
                        onClick={() => {
                          setDeleteSuccessor(true);
                          setEnableSaveButton(true);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </div>
          )}
        </Grid>
        {props.editMode && (
          <Grid item xs={4}>
            <div className={classes.submitButton}>
              <Button
                variant='contained'
                color='primary'
                size='small'
                disabled={!enableSaveButton}
                onClick={() => setShowSuccessorSubmitDialog(true)}
                startIcon={<Save />}
              >
                {locale.saveButton}
              </Button>
              <IconButton
                color='secondary'
                variant='contained'
                disabled={!enableSaveButton}
                component='span'
                aria-label='function-reset'
                onClick={refreshSuccessor}
              >
                <Cancel />
              </IconButton>
            </div>
          </Grid>
        )}
      </Grid>
      {props.editMode && (
        <NotarySuccessorDialog
          open={openSuccessorDialog}
          accessDate={dialogAccessDate}
          successor={dialogSuccessor}
          checked={acceptSuccessorSignature}
          handleSearchChange={(_, successor) => setDialogSuccessor(successor)}
          handleDateChange={(date) => {
            setDialogAccessDate(moment(date).format(locale.dateFormat));
          }}
          handleClose={() => setOpenSuccessorDialog(false)}
          handleSubmit={handleSuccessorSelection}
        />
      )}
      {props.editMode && (
        <ConfirmationDialog
          open={showSuccessorSubmitDialog}
          dialogText={locale.successorDescription}
          title={locale.submitSuccessorDialogTitle}
          handleClose={() => setShowSuccessorSubmitDialog(false)}
          handleSubmit={() => save()}
          detailList={deleteSuccessor ? [] : [successorDescription()]}
        />
      )}
    </Paper>
  );
}

/**
 * Map the properties.
 *
 * @param {*} dispatch
 * @return {Function} - The add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    dispatch: dispatch
  };
}

/**
 * Map the state to the properties.
 *
 * @param {Object} state - The state from store.
 * @return {Object} - The state.
 */
const mapStateToProps = (state) => {
  return {
    user: state.user
  };
};

NotarySuccessor.propTypes = {
  dispatch: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
  notaryId: PropTypes.number.isRequired,
  user: PropTypes.object.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(NotarySuccessor);
